import axios from 'axios'
import { captureException } from '@sentry/browser'
import { Preferences } from '@capacitor/preferences'

async function getPreference (key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

async function setPreference (key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

async function removePreference (key) {
  return await Preferences.remove({ key: key })
}

export const state = {
    insightsReports: null,
    reportDates: [],
    insightsReportsLoaded: false,
    reportDatesLoaded: false
}

const initializePreferences = async () => {
  try {
    const insightsReports = await getPreference('insightsReports');
    if (insightsReports) {
      state.insightsReports = insightsReports;
      state.insightsReportsLoaded = true;
    }
  } catch (error) {
    captureException(error)
  }
  
  try {
    const reportDates = await getPreference('reportDates');
    if (reportDates) {
      state.reportDates = reportDates;
      state.reportDatesLoaded = true;
    }
  } catch (error) {
    captureException(error)
  }
};

initializePreferences();

export const getters= {
    insightsReports: state => state.insightsReports,
    reportDates: state => state.reportDates,
    isReportsDataLoaded: state => state.insightsReportsLoaded && state.reportDatesLoaded,
    nextReportDay: (state, getters, rootState) => {
      const today = new Date();      
        // If we don't have insights reports, we can't calculate the next report day
        if (!state.insightsReports || !state.insightsReports.created_at) {
            // Use user creation date from auth store instead
            const user = rootState.auth.user;
            if (!user || !user.created_at) {
                return null;
            }
            
            const userCreationDate = new Date(user.created_at);
            const nextReportDate = new Date(userCreationDate);
            nextReportDate.setDate(userCreationDate.getDate() + 7);
            
            // If the next report date is in the past, there's no upcoming report
            if (nextReportDate < today) {
                // Use today's date if next report date is in the past
                nextReportDate.setDate(today.getDate());
            }
            
            const dayOfWeek = nextReportDate.getDay();
            return dayOfWeek
        }
        
        const currentReportDate = new Date(state.insightsReports.created_at);
        // Calculate the date for the next report (7 days after the current report)
        let nextReportDate = new Date(currentReportDate);
        nextReportDate.setDate(currentReportDate.getDate() + 7);
        // If the next report date is in the past, there's no upcoming report
        if (nextReportDate < today) {
          nextReportDate = new Date(today.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
        }
        // Otherwise, return the day of the week using i18n
        return nextReportDate.getDay();
    }
}

export const mutations = {
    SET_INSIGHTS_REPORTS(state, data) {
      state.insightsReports = data
      state.insightsReportsLoaded = true
    },
    SET_REPORT_DATES(state, dates) {
      state.reportDates = dates
      state.reportDatesLoaded = true
    }
}

export const actions = {
    async fetchReports({ dispatch }) {
        await dispatch('fetchInsightsReports')
        await dispatch('fetchReportDates')
    },

    async fetchInsightsReports({ commit }) {
      try {
        // Clear existing insights reports before fetching fresh data from DB
        commit('SET_INSIGHTS_REPORTS', null)
        
        const response = await axios.get('/insights-reports/latest')
        commit('SET_INSIGHTS_REPORTS', response.data)
        await setPreference('insightsReports', response.data)
      } catch (error) {
        if (error.response?.status !== 404) {
          captureException(error)
        }
      }
    },

    async fetchReportDates({ commit }) {
      try {
        // Clear existing report dates before fetching fresh data from DB
        commit('SET_REPORT_DATES', [])
        
        const response = await axios.get('/insights-reports/all-dates')
        commit('SET_REPORT_DATES', response.data)
        await setPreference('reportDates', response.data)
      } catch (error) {
        if (error.response?.status !== 404) {
          captureException(error)
        }
      }
    }
}